import axios from 'axios'
import * as Sentry from '@sentry/react'
import { getConnectUser } from 'helpers/getConnectUser'
import { cleanup } from 'helpers/cleanup'
import {
    SENTRY_SMOKE_TEST_TAG,
    SENTRY_SMOKE_TEST_FINGERPRINT,
    SMOKE_TEST_HEADER
} from 'helpers/sentrySmokeTestConstants'

// 5xx: always a genuine server-side failure, reported centrally as an issue.
const REPORTABLE_HTTP_STATUS_THRESHOLD = 500

// Only 400 (malformed request = a real client bug) is worth reporting among
// 4xx; 401/403/404/409/422 are expected flows the callers already handle.
const REPORTABLE_CLIENT_ERROR_STATUSES = new Set([400])

const isReportableResponseStatus = (status) =>
    status >= REPORTABLE_HTTP_STATUS_THRESHOLD ||
    REPORTABLE_CLIENT_ERROR_STATUSES.has(status)

const classifyRequestError = (error) =>
    error.code === 'ECONNABORTED' ? 'timeout' : 'network'

// Marks smoke-test requests so their Sentry signal can be grouped and kept
// out of real triage. Issue form (tags + fingerprint).
const smokeTestReportContext = (config) => {
    if (config?.headers?.[SMOKE_TEST_HEADER] !== 'true') {
        return {}
    }

    return {
        tags: { [SENTRY_SMOKE_TEST_TAG]: 'true' },
        fingerprint: [SENTRY_SMOKE_TEST_FINGERPRINT]
    }
}

// Same marker, flattened for the Logs API (single attributes object).
const smokeTestLogAttributes = (config) =>
    config?.headers?.[SMOKE_TEST_HEADER] === 'true'
        ? { [SENTRY_SMOKE_TEST_TAG]: 'true' }
        : {}

const isExternalAbsoluteUrl = (url, baseURL) => {
    if (!url || !/^https?:\/\//i.test(url)) {
        return false
    }

    if (!baseURL) {
        return true
    }

    try {
        return new URL(url).origin !== new URL(baseURL).origin
    } catch {
        return true
    }
}

const reportRequestError = (error) => {
    // Offline is expected/noisy for an offline-first app - skip.
    if (navigator.onLine === false) {
        return
    }

    // Log, not issue: network/timeout are high-volume and rarely actionable
    // per-event; keep them queryable/alertable-by-volume without issue noise.
    Sentry.logger.warn(
        `Request ${classifyRequestError(error)} failure for ${
            error.config?.method
        } ${error.config?.url?.split('?')[0]}`,
        {
            error_type: classifyRequestError(error),
            endpoint: error.config?.url?.split('?')[0],
            method: error.config?.method,
            ...smokeTestLogAttributes(error.config)
        }
    )
}

const reportBadResponse = (response) => {
    // 5xx: genuine server failure - report as an issue for direct alerting.
    if (response.status >= REPORTABLE_HTTP_STATUS_THRESHOLD) {
        const { tags: smokeTestTags, fingerprint } = smokeTestReportContext(
            response.config
        )

        Sentry.captureException(
            new Error(
                `HTTP ${response.status} from ${response.config?.url?.split('?')[0]}`
            ),
            {
                tags: {
                    error_type: 'bad_response',
                    http_status: response.status,
                    endpoint: response.config?.url?.split('?')[0],
                    method: response.config?.method,
                    ...smokeTestTags
                },
                ...(fingerprint && { fingerprint })
            }
        )
        return
    }

    // 400: a real client bug, but lower-signal/higher-volume than 5xx - log it.
    Sentry.logger.warn(
        `HTTP ${response.status} from ${response.config?.url?.split('?')[0]}`,
        {
            error_type: 'client_error',
            http_status: response.status,
            endpoint: response.config?.url?.split('?')[0],
            method: response.config?.method,
            ...smokeTestLogAttributes(response.config)
        }
    )
}

const connectApi = () => {
    const axiosInstance = axios.create({
        baseURL: process.env.REACT_APP_API_BASE_URL,
        headers: {
            'MyAgro-Application-Name': 'connect-v2'
        }
    })

    // eslint-disable-next-line space-before-function-paren
    axiosInstance.interceptors.request.use(async (config) => {
        const path = window.location.pathname
        const { token } = getConnectUser()

        if (path !== '/login' && !token) {
            await cleanup()
            return (window.location.href = '/login')
        }

        // Absolute third-party URLs (e.g. smoke-test httpstat.us) must not
        // receive the signed-in user's bearer token.
        if (isExternalAbsoluteUrl(config.url, config.baseURL)) {
            return config
        }

        config.headers.Authorization = `Bearer ${token}`
        return config
    })

    axiosInstance.interceptors.response.use(
        (response) => response,
        async (error) => {
            // enrollments/today uses 403 for a zone mismatch. The VE profile
            // requests it on open, and a forbidden count should stay on the
            // page instead of ending the session.
            const requestUrl =
                error.config?.url || error.response?.config?.url || ''
            const isEnrollmentToday = requestUrl.includes(
                '/enrollments/today/'
            )
            if (
                error.response?.status === 403 &&
                window.location.pathname !== '/login' &&
                !isEnrollmentToday
            ) {
                await cleanup()
                return (window.location.href = '/login')
            }

            // Resolve HTTP error responses (callers inspect `.status`), but
            // reject true network/timeout failures so they aren't mistaken for
            // a successful response.
            if (error.response) {
                if (isReportableResponseStatus(error.response.status)) {
                    reportBadResponse(error.response)
                }
                return error.response
            }

            reportRequestError(error)

            return Promise.reject(error)
        }
    )

    return axiosInstance
}

export default connectApi()
